import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import TextInput from "../../../../components/common/TextInput";
import { PlusIcon } from "../../../../icons";
import { usePlanning, } from "../../context/PlanningContext";
import { UserInterviewBlock, } from "../../../../components/chat/UserInterviewBlock";
// @ts-ignore
import dotsAnimation from "../../../../../assets/dots.json";
const plannerWindow = window;
const CreatePlan = ({ onNext: _onNext, onComplete, onMessageSubmit, onGenerateTestPlan, onProcessingChange, initialValue, slideCount, styleGuide, parsedStyleGuide, }) => {
    // Get plan persistence methods from context
    const { currentPlanId, savePlanVersion } = usePlanning();
    const [description, setDescription] = useState(initialValue || "");
    const [selectedSuggestionTitle, setSelectedSuggestionTitle] = useState(undefined);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [interviewData, setInterviewData] = useState(null);
    // Load lottie-player script if not already loaded
    useEffect(() => {
        if (!customElements.get("lottie-player")) {
            const script = document.createElement("script");
            script.src =
                "https://unpkg.com/@lottiefiles/lottie-player@latest/dist/lottie-player.js";
            document.head.appendChild(script);
        }
    }, []);
    // Notify parent when processing state changes
    useEffect(() => {
        if (onProcessingChange) {
            onProcessingChange(isProcessing);
        }
    }, [isProcessing, onProcessingChange]);
    const suggestions = [
        {
            id: "series-a",
            title: "Series A Pitch",
            description: "Fundraising deck for investors",
            prompt: "Create a Series A pitch deck highlighting our product vision, market opportunity, business model, traction metrics, and funding ask.",
        },
        {
            id: "market-analysis",
            title: "Market Analysis",
            description: "Industry trends and opportunities",
            prompt: "Build a market analysis presentation covering market size, growth trends, competitive landscape, and white-space opportunities.",
        },
        {
            id: "sales-enablement",
            title: "Sales Enablement",
            description: "Product positioning for enterprise",
            prompt: "Design a sales enablement deck for enterprise customers with pain points, value messaging, differentiated features, ROI evidence, and customer stories.",
        },
        {
            id: "investor-update",
            title: "Investor Update",
            description: "Quarterly performance review",
            prompt: "Prepare a quarterly investor update summarizing financial performance, key KPIs, strategic initiatives, wins, challenges, and outlook.",
        },
    ];
    const handleSuggestionClick = (suggestion) => {
        setDescription(suggestion.prompt);
        setSelectedSuggestionTitle(suggestion.title);
    };
    // Generate a test plan for quick UI development without LLM calls
    const generateTestPlan = (targetSlideCount) => {
        const testPlan = {
            id: `test-plan-${Date.now()}`,
            slideCount: targetSlideCount,
            version: 1,
            sections: [
                {
                    id: "section-1",
                    name: "Introduction",
                    purpose: "Opening section with background and context",
                    isCollapsed: false,
                    order: 0,
                    slides: [
                        {
                            id: "slide-1-1",
                            name: "Welcome & Overview",
                            purpose: "Introduce the presentation and set expectations",
                            order: 0,
                        },
                        {
                            id: "slide-1-2",
                            name: "Agenda",
                            purpose: "Outline key topics we'll cover",
                            order: 1,
                        },
                    ],
                },
                {
                    id: "section-2",
                    name: "Main Content",
                    purpose: "Core information and analysis",
                    isCollapsed: false,
                    order: 1,
                    slides: [
                        {
                            id: "slide-2-1",
                            name: "Key Findings",
                            purpose: "Present the most important insights",
                            order: 0,
                        },
                        {
                            id: "slide-2-2",
                            name: "Data Analysis",
                            purpose: "Deep dive into supporting data",
                            order: 1,
                        },
                        {
                            id: "slide-2-3",
                            name: "Market Trends",
                            purpose: "Show industry trends and patterns",
                            order: 2,
                        },
                    ],
                },
                {
                    id: "section-3",
                    name: "Recommendations",
                    purpose: "Strategic recommendations based on findings",
                    isCollapsed: false,
                    order: 2,
                    slides: [
                        {
                            id: "slide-3-1",
                            name: "Proposed Strategy",
                            purpose: "Outline recommended approach",
                            order: 0,
                        },
                        {
                            id: "slide-3-2",
                            name: "Implementation Plan",
                            purpose: "Steps to execute the strategy",
                            order: 1,
                        },
                    ],
                },
                {
                    id: "section-4",
                    name: "Conclusion",
                    purpose: "Wrap-up and next steps",
                    isCollapsed: false,
                    order: 3,
                    slides: [
                        {
                            id: "slide-4-1",
                            name: "Summary",
                            purpose: "Recap key points",
                            order: 0,
                        },
                        {
                            id: "slide-4-2",
                            name: "Next Steps",
                            purpose: "Define action items and timeline",
                            order: 1,
                        },
                    ],
                },
            ],
            metadata: {
                createdAt: Date.now(),
                lastModified: Date.now(),
            },
        };
        // Adjust slide count to match target
        let currentSlideCount = testPlan.sections.reduce((total, section) => total + section.slides.length, 0);
        if (currentSlideCount < targetSlideCount) {
            // Add more slides to the last section
            const lastSection = testPlan.sections[testPlan.sections.length - 1];
            const slidesToAdd = targetSlideCount - currentSlideCount;
            for (let i = 0; i < slidesToAdd; i++) {
                lastSection.slides.push({
                    id: `slide-extra-${i + 1}`,
                    name: `Additional Slide ${i + 1}`,
                    purpose: `Extra content slide for testing`,
                    order: lastSection.slides.length,
                });
            }
        }
        else if (currentSlideCount > targetSlideCount) {
            // Remove slides from the end
            let slidesToRemove = currentSlideCount - targetSlideCount;
            for (let i = testPlan.sections.length - 1; i >= 0 && slidesToRemove > 0; i--) {
                const section = testPlan.sections[i];
                while (section.slides.length > 1 && slidesToRemove > 0) {
                    section.slides.pop();
                    slidesToRemove--;
                }
            }
        }
        // Recalculate final slide count
        currentSlideCount = testPlan.sections.reduce((total, section) => total + section.slides.length, 0);
        testPlan.slideCount = currentSlideCount;
        return testPlan;
    };
    const handleSubmit = async () => {
        if (description.trim() && !isProcessing) {
            console.log("[CreatePlan] Submitting prompt:", description);
            const submittedDescription = description;
            const submittedTitle = selectedSuggestionTitle;
            // Clear the input immediately
            setDescription("");
            setSelectedSuggestionTitle(undefined);
            setProcessingMessage(submittedDescription);
            // Notify parent immediately that message was submitted
            if (onMessageSubmit) {
                onMessageSubmit(submittedDescription, submittedTitle);
            }
            setIsProcessing(true);
            try {
                console.log("[CreatePlan] Starting plan generation with Planner Agent");
                // Check if planner service is available on window
                if (!plannerWindow.__sendMessageToPlanner) {
                    throw new Error("Planner service not initialized. Window bridge not available.");
                }
                // Create planning conversation if createPlanningConversation is available
                if (plannerWindow.__createPlanningConversation && styleGuide) {
                    console.log("[CreatePlan] Creating planning conversation with styleGuide:", styleGuide);
                    const jobId = plannerWindow.__createPlanningConversation({
                        type: styleGuide.type,
                        documentId: styleGuide.documentId,
                        path: styleGuide.path,
                        label: styleGuide.label,
                    });
                    console.log("[CreatePlan] Created planning job:", jobId);
                }
                let generatedPlan = null;
                // Build the styleGuide parameter for the planner
                const styleGuideParam = styleGuide
                    ? {
                        type: styleGuide.type,
                        documentId: styleGuide.documentId,
                        path: styleGuide.path,
                        label: styleGuide.label,
                    }
                    : undefined;
                // Log parsed style guide info for debugging
                if (parsedStyleGuide) {
                    console.log("[CreatePlan] Including parsed style guide context:", {
                        slidesCount: parsedStyleGuide.slides?.length || 0,
                        layoutsUsed: parsedStyleGuide.layouts_used?.length || 0,
                        sectionsCount: parsedStyleGuide.sections?.length || 0,
                    });
                }
                // Call Planner Agent through window bridge
                const response = await plannerWindow.__sendMessageToPlanner(submittedDescription, {
                    slideCount: parseInt(slideCount) || 20,
                    styleGuide: styleGuideParam,
                    parsedStyleGuide: parsedStyleGuide || undefined,
                }, {
                    onChunk: (chunk) => {
                        console.log("[CreatePlan] Received text chunk:", chunk);
                    },
                    onToolCall: (toolName, args) => {
                        console.log("[CreatePlan] Tool call started:", toolName, args);
                        // Handle userInterview tool - show interview UI to collect user responses
                        if (toolName === "userInterview") {
                            // Args may come as a string or object - parse if needed
                            let parsedArgs = args;
                            if (typeof args === "string") {
                                try {
                                    parsedArgs = JSON.parse(args);
                                }
                                catch (e) {
                                    console.error("[CreatePlan] Failed to parse userInterview args:", e);
                                    return;
                                }
                            }
                            if (parsedArgs?.questions) {
                                const interview = {
                                    id: `interview-${Date.now()}`,
                                    questions: parsedArgs.questions,
                                    currentQuestionIndex: 0,
                                    answers: [],
                                    status: "active",
                                };
                                console.log("[CreatePlan] Setting interview data:", interview);
                                setInterviewData(interview);
                            }
                        }
                    },
                    onToolResult: (toolName, result) => {
                        console.log("[CreatePlan] Tool result:", toolName, result);
                        // Clear interview data when userInterview completes
                        if (toolName === "userInterview") {
                            setInterviewData(null);
                        }
                    },
                    onPlanChange: async (plan, action) => {
                        console.log(`[CreatePlan] Plan ${action}:`, plan);
                        generatedPlan = plan;
                        // Persist the plan to the backend
                        if (currentPlanId) {
                            try {
                                console.log(`[CreatePlan] Saving plan to backend for planId: ${currentPlanId}`);
                                // Convert PresentationPlan to the format expected by the API
                                // Include the original prompt for resume functionality
                                const planJson = {
                                    sections: plan.sections.map((section) => ({
                                        id: section.id,
                                        name: section.name,
                                        purpose: section.purpose,
                                        slides: section.slides.map((slide) => ({
                                            id: slide.id,
                                            name: slide.name,
                                            purpose: slide.purpose,
                                        })),
                                    })),
                                    totalSlides: plan.sections.reduce((acc, s) => acc + s.slides.length, 0),
                                    totalSections: plan.sections.length,
                                    // Save the original prompt/description for resume
                                    prompt: submittedDescription,
                                    slideCount: parseInt(slideCount) || 0,
                                };
                                // Generate title for display in drafts - use submitted title or first few words of prompt
                                let planTitle = submittedTitle;
                                if (!planTitle && submittedDescription) {
                                    const words = submittedDescription
                                        .split(/\s+/)
                                        .slice(0, 8)
                                        .join(" ");
                                    planTitle =
                                        words.length > 40
                                            ? words.substring(0, 40) + "..."
                                            : words;
                                }
                                const result = await savePlanVersion(currentPlanId, planJson, parseInt(slideCount) || undefined, "Initial plan created", // Label for version history
                                planTitle);
                                if (result) {
                                    console.log(`[CreatePlan] Plan saved successfully, version: ${result.versionId}`);
                                }
                                else {
                                    console.warn("[CreatePlan] Failed to save plan to backend");
                                }
                            }
                            catch (error) {
                                console.error("[CreatePlan] Error saving plan to backend:", error);
                            }
                        }
                        else {
                            console.warn("[CreatePlan] No currentPlanId available, cannot save plan");
                        }
                    },
                });
                console.log("[CreatePlan] Planner Agent response:", response);
                // Check the response action and handle accordingly
                if (response.action === "plan_created" ||
                    response.action === "plan_updated") {
                    if (response.plan) {
                        console.log("[CreatePlan] Plan generation complete, navigating to detailed planning");
                        onComplete(submittedDescription, response.plan, submittedTitle);
                    }
                    else {
                        throw new Error("Plan was created but not returned in response");
                    }
                }
                else if (response.action === "conversation") {
                    // Agent responded with conversation (no plan yet)
                    console.log("[CreatePlan] Agent response (no plan):", response.content);
                    setIsProcessing(false);
                    // Could show the agent's response to the user here
                }
                else if (response.action === "error") {
                    throw new Error(response.content || "Failed to generate plan");
                }
            }
            catch (error) {
                console.error("[CreatePlan] Failed to generate plan:", error);
                setIsProcessing(false);
                // Office.js doesn't support alert() - log error instead
                console.error("[CreatePlan] Error details:", error instanceof Error ? error.message : String(error));
            }
        }
    };
    const handleGenerateTestPlan = () => {
        if (!isProcessing) {
            const testDescription = "Test Plan - UI Development (No LLM)";
            // Clear the input immediately
            setDescription("");
            setSelectedSuggestionTitle(undefined);
            setProcessingMessage(testDescription);
            // Notify parent immediately that message was submitted
            if (onMessageSubmit) {
                onMessageSubmit(testDescription, "Test Plan");
            }
            setIsProcessing(true);
            // Generate test plan immediately
            const testPlan = generateTestPlan(parseInt(slideCount) || 10);
            // Small delay to show the processing state
            setTimeout(() => {
                onComplete(testDescription, testPlan, "Test Plan");
            }, 1500);
        }
    };
    // Handle interview submission
    const handleInterviewSubmit = (answers) => {
        console.log("[CreatePlan] Interview answers submitted:", answers);
        // Dispatch the carousel:interview-submit event that the planner service listens for
        const event = new CustomEvent("carousel:interview-submit", {
            detail: {
                interviewId: interviewData?.id,
                answers: answers,
            },
        });
        window.dispatchEvent(event);
        // Clear interview data - it will be cleared by onToolResult when the tool completes
        // But we can update the status to show it's been submitted
        if (interviewData) {
            setInterviewData({
                ...interviewData,
                status: "completed",
                answers: answers,
            });
        }
    };
    return (_jsxs("div", { className: "flex flex-col gap-6 -mt-4", children: [isProcessing ? (
            /* Processing state - show as chat layout */
            _jsxs("div", { className: "flex flex-col gap-4", children: [_jsx("div", { className: "flex gap-3 items-start justify-end", children: _jsx("div", { className: "flex flex-col gap-2 max-w-[70%] bg-gray-100 rounded-lg p-4", children: _jsx("p", { className: "text-[13px] text-gray-990 leading-5", children: processingMessage }) }) }), _jsx("div", { className: "flex gap-3 items-start", children: _jsx("div", { className: "flex flex-col gap-2 w-full", children: interviewData && interviewData.status === "active" ? (
                            /* Show interview questions when userInterview tool is active */
                            _jsxs(_Fragment, { children: [_jsx("p", { className: "text-[13px] leading-5 text-gray-990", children: "I have a few questions to help create the best presentation for you:" }), _jsx(UserInterviewBlock, { data: interviewData, onSubmit: handleInterviewSubmit })] })) : (
                            /* Show thinking indicator when no interview is active */
                            _jsxs(_Fragment, { children: [_jsx("p", { className: "text-[13px] leading-5 text-gray-990", children: "Carousel is preparing the slide plan. Please wait a few seconds." }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-[13px] text-primary-purple-60", children: "Thinking" }), _jsx("div", { className: "w-6 h-6", children: _jsx("lottie-player", { src: JSON.stringify(dotsAnimation), background: "transparent", speed: "1", style: { width: "24px", height: "24px" }, loop: true, autoplay: true }) })] })] })) }) })] })) : (
            /* Default state - show heading and suggestions */
            _jsxs(_Fragment, { children: [_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx("h2", { className: "text-[19px] font-bold leading-5 text-gray-990", children: "Let's create a presentation outline" }), _jsx("p", { className: "text-[13px] text-gray-600 leading-5 mt-2", children: "Describe what you'd like to create so that you can just review and revise instead of thinking of something new." })] }), _jsxs("div", { className: "flex flex-col gap-3", children: [_jsx("h3", { className: "text-[13px] font-bold text-gray-600", children: "Suggestions" }), _jsx("div", { className: "grid grid-cols-2 gap-3", children: suggestions.map((suggestion) => (_jsxs("div", { onClick: () => handleSuggestionClick(suggestion), className: "flex flex-col p-4 gap-1 rounded-lg border border-gray-300 bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors", children: [_jsx("h4", { className: "text-[13px] font-bold text-gray-990", children: suggestion.title }), _jsx("p", { className: "text-[13px] text-gray-800", children: suggestion.description })] }, suggestion.id))) })] })] })), !isProcessing && (_jsx(TextInput, { value: description, onChange: (value) => {
                    setDescription(value);
                    // Clear selected suggestion title if user manually edits
                    if (selectedSuggestionTitle) {
                        setSelectedSuggestionTitle(undefined);
                    }
                }, onSubmit: handleSubmit, placeholder: "Describe your presentation" })), onGenerateTestPlan && !isProcessing && (_jsxs("div", { className: "flex flex-col gap-2 pt-4 border-t border-gray-300", children: [_jsx("p", { className: "text-[11px] text-gray-500 italic", children: "Development shortcut (bypasses LLM)" }), _jsxs("button", { onClick: handleGenerateTestPlan, disabled: isProcessing, className: "flex items-center justify-center gap-2 px-4 py-2.5 text-[13px] font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer", children: [_jsx(PlusIcon, { size: 16, color: "currentColor" }), "Generate Test Plan (No LLM)"] })] }))] }));
};
export default CreatePlan;
